import { Menu } from 'lucide-react';
import type { RoomView } from '../nav';
import { RailPanel, panelLabel } from './RailPanel';

/**
 * The section nav, on a phone.
 *
 * The rail's nav is seven entries and it does not fit 430px, so it used to scroll sideways: an
 * operator saw about one and a half labels and had to drag the row to find out what the others
 * were. Two things were wrong with that beyond the awkwardness — the header never said **which
 * section was on screen**, and a section the room has not enabled was a grey word whose only
 * explanation was a `title` tooltip, which **does not exist on touch**. Both are fixed by making
 * the list a panel: the header carries the current section's name instead of the row, and a
 * disabled entry can say why in words.
 *
 * From `md` up this is not rendered and the horizontal nav is unchanged — see `RoomShell`, which
 * holds both arrangements.
 *
 * Navigation is `onNavigate`, the same already-guarded callback the desktop nav calls: `App` wraps
 * it in `leave`, so a section change out of an editor asks before discarding. Nothing about that
 * is repeated here.
 */

export interface SectionEntry {
  view: RoomView;
  label: string;
  enabled: boolean;
  count?: number;
}

export function SectionMenu({
  entries,
  view,
  onNavigate,
}: {
  entries: readonly SectionEntry[];
  view: RoomView;
  onNavigate: (view: RoomView) => void;
}) {
  return (
    <RailPanel
      icon={<Menu className="size-5" aria-hidden />}
      label="Sections"
      heading={<span className={`block ${panelLabel}`}>Sections</span>}
    >
      {(close) => (
        <nav aria-label="Room sections" className="pb-1.5">
          {entries.map((entry) => {
            const count =
              typeof entry.count === 'number' ? (
                <span className="num shrink-0 text-[12px] font-bold text-white/40">
                  {entry.count}
                </span>
              ) : null;

            if (!entry.enabled) {
              return (
                // Not a button, and the reason is text rather than a `title`. A tooltip is
                // unreachable on a touch screen, which left this as an unexplained grey word on
                // the one size where the explanation matters most.
                <div
                  key={entry.view}
                  className="flex min-h-[44px] items-center justify-between gap-3 px-4 py-2"
                >
                  <span className="text-[13.5px] font-semibold uppercase tracking-[0.06em] text-white/25">
                    {entry.label}
                  </span>
                  <span className="shrink-0 text-[10px] uppercase tracking-[0.08em] text-white/25">
                    Not enabled
                  </span>
                </div>
              );
            }

            const current = view === entry.view;
            return (
              <button
                key={entry.view}
                type="button"
                onClick={() => {
                  close();
                  onNavigate(entry.view);
                }}
                aria-current={current ? 'page' : undefined}
                // The current section is marked in chalk, as it is in the rail — a 3px rail down
                // the leading edge rather than under the label, which is the same mark turned
                // through ninety degrees with the list.
                className={`flex min-h-[44px] w-full items-center justify-between gap-3 px-4 py-2 text-left transition ${
                  current
                    ? 'bg-white/5 text-white shadow-[inset_3px_0_0_#ffffff]'
                    : 'text-rail-100 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span className="truncate text-[13.5px] font-semibold uppercase tracking-[0.06em]">
                  {entry.label}
                </span>
                {count}
              </button>
            );
          })}
        </nav>
      )}
    </RailPanel>
  );
}
